'use es6';

import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import { forwardRef, useState, useEffect } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { AddIcon } from 'foundations-assets';
import withVisibilityChecker from 'simple-menus-lib/components/menu/decorators/withVisibilityChecker';
import withPromotingMenuDropTargets from 'simple-menus-lib/components/menu/decorators/withPromotingMenuDropTargets';
import withConnectedMenuNodeDivider from 'simple-menus-lib/components/menu/decorators/withConnectedMenuNodeDivider';
import menuDividerDropTarget from 'simple-menus-lib/components/dnd/decorators/menuDividerDropTarget';
import { MENU_TREE_INDENTATION, MENU_DIVIDER_MARGIN_OFFSET } from '../../constants/menus';
import { NodeDividerMenuActions } from '../../constants/nodeDividerMenuActions';
import UsageTracker from 'simple-menus-lib/utils/usageTracker/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const actions = [NodeDividerMenuActions.NO_LINK, NodeDividerMenuActions.PAGE_LINK, NodeDividerMenuActions.PAGE_LINK_WITH_PARAMS, NodeDividerMenuActions.URL_LINK];
const DividerActionsMenuButton = props => {
  const rest = Object.assign({}, (_objectDestructuringEmpty(props), props));
  return /*#__PURE__*/_jsx(IconButton, Object.assign({}, rest, {
    shape: "circle",
    children: /*#__PURE__*/_jsx(AddIcon, {})
  }));
};
const DividerActionsMenu = ({
  addMenuNode,
  closeMenu,
  menuIsOpen,
  onMenuOpenChange
}) => {
  const handleNodeCreate = type => {
    addMenuNode(type);
    UsageTracker.track('addMenuItem', {
      type,
      method: 'divider'
    });
    closeMenu();
  };
  const handlers = {
    [NodeDividerMenuActions.NO_LINK]: () => handleNodeCreate(NodeDividerMenuActions.NO_LINK),
    [NodeDividerMenuActions.PAGE_LINK]: () => handleNodeCreate(NodeDividerMenuActions.PAGE_LINK),
    [NodeDividerMenuActions.PAGE_LINK_WITH_PARAMS]: () => {
      handleNodeCreate(NodeDividerMenuActions.PAGE_LINK_WITH_PARAMS);
    },
    [NodeDividerMenuActions.URL_LINK]: () => handleNodeCreate(NodeDividerMenuActions.URL_LINK)
  };
  const buttons = actions.map(action => {
    return /*#__PURE__*/_jsx(Button, {
      onClick: handlers[action],
      use: "unstyled",
      children: unescapedText(`simpleMenusLib.components.menuItemDivider.addItems.${action}`)
    }, action);
  });
  return /*#__PURE__*/_jsx(Dropdown, {
    arrowSize: "small",
    Button: DividerActionsMenuButton,
    buttonSize: "extra-small",
    buttonUse: "tertiary",
    caretRenderer: null,
    open: menuIsOpen,
    onOpenChange: onMenuOpenChange,
    closeOnOutsideClick: false,
    children: /*#__PURE__*/_jsx(List, {
      wrapChildren: true,
      children: buttons
    })
  });
};
const MenuNodeDivider = /*#__PURE__*/forwardRef((props, ref) => {
  const [isHovering, setIsHovering] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const {
    depth,
    depthToNextSibling,
    dropTargets,
    canDrop,
    hasChildren,
    isDragging,
    isLastChild,
    isOver,
    isParentDragging,
    readOnly,
    addNode,
    connectDropTarget
  } = props;
  const hasDropTargets = !(hasChildren || !isLastChild || !depth || !isDragging);
  const showMenuButton = !readOnly && menuIsOpen || !readOnly && isHovering && !isOver && !isDragging && !isParentDragging;
  const showDivider = !readOnly && (menuIsOpen || isHovering || isOver);
  let dividerBackgroundColor = 'fill-primary-default';
  if (isOver && !canDrop) {
    dividerBackgroundColor = 'fill-alert-default';
  } else if (isOver) {
    dividerBackgroundColor = 'fill-info-default';
  }
  let dividerWidth = `calc(100% + ${(depth + MENU_DIVIDER_MARGIN_OFFSET) * MENU_TREE_INDENTATION}px)`;
  let dividerInlineStartPosition = `-${(depth + MENU_DIVIDER_MARGIN_OFFSET) * MENU_TREE_INDENTATION}px`;
  if (isDragging && !hasDropTargets) {
    dividerWidth = '100%';
    dividerInlineStartPosition = '0';
  }
  if (isDragging && hasDropTargets) {
    dividerWidth = `calc(100% + ${depthToNextSibling * MENU_TREE_INDENTATION}px)`;
    dividerInlineStartPosition = `-${depthToNextSibling * MENU_TREE_INDENTATION}px`;
  }
  useEffect(() => {
    const handleClickOutside = () => {
      if (menuIsOpen) {
        setMenuIsOpen(false);
      }
    };
    const handleScroll = () => {
      if (menuIsOpen) {
        setMenuIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('wheel', handleScroll);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('wheel', handleScroll);
    };
  }, [menuIsOpen]);
  const toggleMenuOpen = () => {
    setMenuIsOpen(isOpen => !isOpen);
  };
  const handleCloseMenu = () => {
    setMenuIsOpen(false);
  };
  const handleMenuOpenChange = e => {
    setMenuIsOpen(e.target.value);
  };
  const handleMouseEnter = () => {
    if (readOnly) return;
    setIsHovering(true);
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  const handleDividerAreaClick = e => {
    e.stopPropagation();
  };
  return connectDropTarget( /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsxs(TFlex, {
      ref: ref,
      align: "center",
      blockSize: "16px",
      inlineSize: dividerWidth,
      maxInlineSize: dividerWidth,
      position: "relative",
      insetInlineStart: dividerInlineStartPosition,
      onClick: handleDividerAreaClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [dropTargets, showMenuButton && /*#__PURE__*/_jsx(DividerActionsMenu, {
        addMenuNode: addNode,
        closeMenu: handleCloseMenu,
        menuIsOpen: menuIsOpen,
        onMenuOpenChange: handleMenuOpenChange
      }), showDivider && /*#__PURE__*/_jsx(View, {
        blockSize: "2px",
        backgroundColor: dividerBackgroundColor,
        display: "flex",
        flex: "1 1 auto",
        onClick: toggleMenuOpen
      })]
    })
  }));
});
MenuNodeDivider.propTypes = {
  depth: PropTypes.number.isRequired,
  depthToNextSibling: PropTypes.number,
  dropTargets: PropTypes.node,
  canDrop: PropTypes.bool,
  hasChildren: PropTypes.bool,
  isDragging: PropTypes.bool,
  isLastChild: PropTypes.bool,
  isOver: PropTypes.bool,
  isParentDragging: PropTypes.bool,
  isVisible: PropTypes.bool,
  readOnly: PropTypes.bool,
  addNode: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired
};
export default compose(withConnectedMenuNodeDivider(), withPromotingMenuDropTargets(), menuDividerDropTarget(), withVisibilityChecker())(MenuNodeDivider);